import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, MessageCircle, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Official MIRO Consultancy contact details
const WHATSAPP_NUMBER = "917601016337";
const CONTACT_PHONE = "+91 76010 16337";
const CONTACT_EMAILS = ["miroconsultancy.in@gmail.com", "support@miroconsultancy.co.in"];

const CATEGORY_TABS = [
  { value: "all",                 label: "All Courses" },
  { value: "flagship",            label: "Flagship Programs" },
  { value: "english_test_prep",   label: "English & Test Prep",
    accent: "blue",
    match: (c) => ["ielts", "toefl", "celpip", "pte", "duolingo"].includes(c) },
  { value: "spoken_english",      label: "Spoken English", accent: "green" },
  { value: "german",              label: "German",         accent: "green" },
  { value: "french",              label: "French",         accent: "purple" },
  { value: "aptitude",            label: "GRE / GMAT / CSAT",
    accent: "navy",
    match: (c) => ["gre", "gmat", "csat"].includes(c) },
  { value: "career_development",  label: "Career Development", accent: "amber" },
  { value: "it",                  label: "IT Programs" },
  { value: "non_it",              label: "Non-IT Programs" },
];

const CATEGORY_ACCENTS = {
  ielts: "blue", toefl: "blue", celpip: "blue", pte: "blue", duolingo: "blue",
  spoken_english: "green",
  german: "green",
  french: "purple",
  gre: "navy", gmat: "navy", csat: "navy",
  career_development: "amber",
  flagship: "cyan",
  it: "cyan",
  non_it: "cyan",
};

const ACCENT_CLASS = {
  blue:   { chip: "bg-sky-500/15 text-sky-300",       ring: "hover:ring-sky-400/60" },
  green:  { chip: "bg-emerald-500/15 text-emerald-300", ring: "hover:ring-emerald-400/60" },
  purple: { chip: "bg-fuchsia-500/15 text-fuchsia-300", ring: "hover:ring-fuchsia-400/60" },
  navy:   { chip: "bg-indigo-500/15 text-indigo-300",  ring: "hover:ring-indigo-400/60" },
  amber:  { chip: "bg-amber-500/15 text-amber-300",    ring: "hover:ring-amber-400/60" },
  cyan:   { chip: "bg-cyan-500/15 text-cyan-300",      ring: "hover:ring-cyan-400/60" },
};

const CATEGORY_LABEL = {
  it: "IT", non_it: "Non-IT", flagship: "Flagship",
  ielts: "IELTS", toefl: "TOEFL", celpip: "CELPIP", pte: "PTE", duolingo: "Duolingo",
  spoken_english: "Spoken English",
  german: "German", french: "French",
  gre: "GRE", gmat: "GMAT", csat: "CSAT",
  career_development: "Career Development",
  corporate_training: "Corporate Training",
  indian_admissions: "Indian Admissions",
  online_degrees: "Online Degrees",
};

const DELIVERY_LABEL = {
  online: "Online", offline: "Offline", hybrid: "Hybrid",
  blended: "Blended", online_or_blended: "Online / Blended",
};

const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");

function feeString(p) {
  const prefix = p.starts_from ? "Starts from " : "";
  const suffix = p.gst_applicable ? " + GST" : "";
  return `${prefix}${inr(p.fee)}${suffix}`;
}

export default function PublicCatalogue() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "all");
  const [q, setQ] = useState("");
  const [deliveryMode, setDeliveryMode] = useState("all");
  const [feeMin, setFeeMin] = useState("");
  const [feeMax, setFeeMax] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enquiry, setEnquiry] = useState(null); // program obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (q) p.q = q;
      if (deliveryMode !== "all") p.delivery_mode = deliveryMode;
      if (feeMin) p.fee_min = Number(feeMin);
      if (feeMax) p.fee_max = Number(feeMax);
      const r = await axios.get(`${API}/public/catalogue`, { params: p });
      setItems(r.data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("Catalogue load failed:", e);
      }
    }
    finally { setLoading(false); }
  }, [q, deliveryMode, feeMin, feeMax]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    const t = CATEGORY_TABS.find(x => x.value === tab);
    if (!t) return items;
    if (t.match) return items.filter(p => t.match(p.category));
    return items.filter(p => p.category === tab);
  }, [items, tab]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(p => {
      const key = p.category || "other";
      (groups[key] ||= []).push(p);
    });
    return groups;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[var(--miro-navy,#041B46)] text-white">
      <Toaster position="top-right" richColors closeButton />
      {/* Header */}
      <header className="border-b border-white/10 bg-[#031132]/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/catalogue" className="flex items-center gap-3">
            <img src="/miro-logo.png" alt="MIRO" className="h-9 w-auto"/>
            <div className="hidden sm:block">
              <div className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">MIRO Academy</div>
              <div className="font-display font-black text-lg leading-none">Course Catalogue</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi MIRO Academy, I'd like more information about your courses.")}`}
               target="_blank" rel="noreferrer"
               data-testid="catalogue-wa-header"
               className="hidden sm:inline-flex items-center gap-2 text-sm text-emerald-300 hover:text-emerald-200 transition-colors">
              <MessageCircle size={16}/> WhatsApp
            </a>
            <Link to="/leadform" className="text-sm text-white/60 hover:text-white">
              General enquiry
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] tracking-[0.2em] uppercase text-cyan-300/80">
            <Sparkles size={12}/> MIRO Academy Course Catalogue
          </div>
          <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Real skills. <span className="bg-gradient-to-r from-cyan-300 to-emerald-300 bg-clip-text text-transparent">Better opportunities.</span>
          </h1>
          <p className="mt-5 text-white/70 text-base sm:text-lg leading-relaxed">
            Professional training, global language programs, test preparation and
            career-development courses designed to help learners build practical
            skills and access better opportunities.
          </p>
        </div>
      </section>

      {/* Category tabs */}
      <section className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap gap-2" data-testid="catalogue-tabs">
          {CATEGORY_TABS.map(t => (
            <button key={t.value} data-testid={`catalogue-tab-${t.value}`}
                    onClick={() => setTab(t.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                      tab === t.value
                        ? "bg-white text-[var(--miro-navy,#041B46)] border-white"
                        : "border-white/15 text-white/70 hover:bg-white/5 hover:text-white"
                    }`}>
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 rounded-xl border border-white/10 bg-white/5">
          <div className="md:col-span-2 flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2 border border-white/10">
            <Search size={15} className="text-white/50"/>
            <input data-testid="catalogue-search" className="bg-transparent outline-none text-sm flex-1 text-white placeholder:text-white/40"
                   placeholder="Search by course name…" value={q} onChange={(e) => setQ(e.target.value)}/>
          </div>
          <div>
            <Label className="text-[11px] text-white/60">Delivery</Label>
            <Select value={deliveryMode} onValueChange={setDeliveryMode}>
              <SelectTrigger data-testid="catalogue-filter-delivery" className="bg-black/30 border-white/10 text-white mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="online_or_blended">Online / Blended</SelectItem>
                <SelectItem value="blended">Blended</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-white/60">Fee min (₹)</Label>
              <Input data-testid="catalogue-fee-min" type="number" min={0} value={feeMin} onChange={e => setFeeMin(e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/>
            </div>
            <div>
              <Label className="text-[11px] text-white/60">Fee max (₹)</Label>
              <Input data-testid="catalogue-fee-max" type="number" min={0} value={feeMax} onChange={e => setFeeMax(e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-7xl mx-auto px-6 mt-10 pb-16 space-y-12" data-testid="catalogue-grid">
        {loading && <div className="text-white/60">Loading catalogue…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 text-white/60">
            No courses match these filters. Try clearing the fee range or search.
          </div>
        )}
        {tab === "all"
          ? Object.entries(grouped).map(([cat, list]) => (
              <CategoryGroup key={cat} category={cat} programs={list} onEnquire={setEnquiry}/>
            ))
          : <CategoryGroup category={tab} programs={filtered} onEnquire={setEnquiry}/>
        }
      </section>

      {/* Admission fee + disclaimer */}
      <section className="max-w-7xl mx-auto px-6 pb-16 space-y-4">
        <div className="rounded-xl border border-white/10 bg-white/5 p-5 flex flex-col md:flex-row md:items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center">
            <ShieldCheck size={20}/>
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg">One-Time Admission Fee — ₹2,000</div>
            <div className="text-sm text-white/70 mt-0.5">
              Applicable to selected MIRO Academy programs. The fee is charged separately from the course fee unless explicitly stated otherwise.
            </div>
          </div>
        </div>

        <div className="text-xs leading-relaxed text-white/60 rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="font-semibold text-white/80 uppercase tracking-wider text-[11px] mb-1">Pricing disclaimer</div>
          Course fees shown are indicative starting prices and may vary based on batch, level, delivery mode, partner, learning materials, examination fees and promotional offers. Admission fees, taxes, examination charges, books and third-party certification costs may be additional unless specifically mentioned. Contact MIRO Academy for the latest approved fee and batch details.
        </div>
        <div className="text-xs leading-relaxed text-white/60 rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="font-semibold text-white/80 uppercase tracking-wider text-[11px] mb-1">Program outcome disclaimer</div>
          Program availability, duration, curriculum, fees, internship structure, certifications, placement assistance and delivery mode may vary by batch and partner. MIRO does not guarantee employment, salary, admission, visa approval, examination scores or any specific career outcome.
        </div>
      </section>

      {/* Footer contact */}
      <footer className="border-t border-white/10 bg-[#03102b]/80">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <img src="/miro-logo.png" alt="MIRO" className="h-8 w-auto mb-3"/>
            <div className="text-white/60">
              MIRO Consultancy — Academy, Study Abroad, Wealth &amp; Digital services.
            </div>
          </div>
          <div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-white/50 mb-2">Talk to us</div>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi MIRO Academy, I'd like more information about your courses.")}`}
               target="_blank" rel="noreferrer"
               data-testid="catalogue-wa-footer"
               className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 transition-colors">
              <MessageCircle size={14}/> WhatsApp {CONTACT_PHONE}
            </a>
            <div className="text-white/60 mt-2">
              <a href={`tel:${WHATSAPP_NUMBER}`} className="hover:text-white">Call {CONTACT_PHONE}</a>
            </div>
          </div>
          <div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-white/50 mb-2">Email</div>
            <div className="flex flex-col gap-1">
              {CONTACT_EMAILS.map(em => (
                <a key={em} href={`mailto:${em}`} className="text-white/70 hover:text-white truncate">
                  {em}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-3 text-[11px] text-white/40 flex flex-wrap justify-between gap-2">
            <div>© {new Date().getFullYear()} MIRO Consultancy. All rights reserved.</div>
            <div>Prices and batches may change. Contact MIRO Academy for the latest details.</div>
          </div>
        </div>
      </footer>

      <EnquiryDialog program={enquiry} onClose={() => setEnquiry(null)}/>
    </div>
  );
}

function CategoryGroup({ category, programs, onEnquire }) {
  if (!programs.length) return null;
  const label = CATEGORY_LABEL[category] || category;
  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <h2 className="font-display font-black text-2xl sm:text-3xl">{label}</h2>
        <span className="text-xs text-white/50">{programs.length} program{programs.length === 1 ? "" : "s"}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {programs.map(p => <CourseCard key={p.id} p={p} onEnquire={onEnquire}/>)}
      </div>
    </div>
  );
}

function CourseCard({ p, onEnquire }) {
  const accent = CATEGORY_ACCENTS[p.category] || "cyan";
  const cls = ACCENT_CLASS[accent] || ACCENT_CLASS.cyan;
  return (
    <div data-testid={`catalogue-card-${p.id}`}
         className={`rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-5 flex flex-col transition-all hover:-translate-y-0.5 hover:ring-2 ${cls.ring}`}>
      <div className="flex items-start gap-2">
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${cls.chip}`}>
          {CATEGORY_LABEL[p.category] || p.category}
        </div>
        {p.featured && (
          <div className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/10 text-white/80">
            <Sparkles size={10}/> Featured
          </div>
        )}
      </div>
      <h3 className="font-display font-bold text-lg mt-3 leading-tight">{p.title}</h3>
      {p.tagline && <div className="text-xs text-white/60 mt-1 italic">{p.tagline}</div>}

      <div className="mt-4 flex items-baseline gap-2">
        <div className={`font-display font-black text-2xl`}>{feeString(p)}</div>
      </div>
      {p.emi_available && <div className="text-[11px] text-emerald-300 mt-1">EMI options available</div>}

      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px] text-white/60">
        <div>Mode<div className="text-white text-sm mt-0.5">{DELIVERY_LABEL[p.delivery_mode] || p.delivery_mode}</div></div>
        {p.duration_verified && (
          <div>Duration<div className="text-white text-sm mt-0.5">{p.duration_value} {p.duration_unit}</div></div>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-white/10 flex gap-2">
        <Button data-testid={`catalogue-enquire-${p.id}`} onClick={() => onEnquire(p)}
                className="flex-1 bg-white text-[var(--miro-navy,#041B46)] hover:bg-white/90">
          Enquire Now
        </Button>
        <Link to={`/catalogue/${p.id}`}
              data-testid={`catalogue-details-${p.id}`}
              className="inline-flex items-center justify-center gap-1 px-3 rounded-md border border-white/15 text-sm text-white/80 hover:bg-white/5">
          View <ArrowRight size={13}/>
        </Link>
      </div>
    </div>
  );
}

/* ---------- Enquiry Dialog (shared by list + detail via a lifted parent) ---------- */
export function EnquiryDialog({ program, onClose }) {
  const empty = useMemo(() => ({
    full_name: "", phone: "", whatsapp: "", email: "", city: "",
    education: "", employment_status: "",
    preferred_delivery_mode: "", preferred_callback_time: "",
    consent: false,
  }), []);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(empty); }, [program?.id, empty]);
  if (!program) return null;
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    if (!f.consent) { toast.error("Please tick the consent checkbox"); return; }
    setSaving(true);
    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      await axios.post(`${API}/public/catalogue/enquiry`, {
        ...f,
        program_id: program.id,
        course_name: program.title,
        page_url: window.location.href,
        utm_source: params.get("utm_source") || undefined,
        utm_medium: params.get("utm_medium") || undefined,
        utm_campaign: params.get("utm_campaign") || undefined,
        utm_content: params.get("utm_content") || undefined,
        utm_term: params.get("utm_term") || undefined,
      });
      toast.success("Enquiry received — a MIRO counsellor will call you soon.");
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0a1f4d] text-white border border-white/10 sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Enquire — {program.title}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2"><Label className="text-white/70">Full name *</Label>
            <Input data-testid="enquiry-name" value={f.full_name} onChange={e => upd("full_name", e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/></div>
          <div><Label className="text-white/70">Phone *</Label>
            <Input data-testid="enquiry-phone" value={f.phone} onChange={e => upd("phone", e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/></div>
          <div><Label className="text-white/70">WhatsApp</Label>
            <Input data-testid="enquiry-whatsapp" value={f.whatsapp} onChange={e => upd("whatsapp", e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/></div>
          <div className="col-span-2"><Label className="text-white/70">Email</Label>
            <Input data-testid="enquiry-email" type="email" value={f.email} onChange={e => upd("email", e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/></div>
          <div><Label className="text-white/70">City</Label>
            <Input value={f.city} onChange={e => upd("city", e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/></div>
          <div><Label className="text-white/70">Education</Label>
            <Input value={f.education} onChange={e => upd("education", e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/></div>
          <div>
            <Label className="text-white/70">Employment</Label>
            <Select value={f.employment_status} onValueChange={v => upd("employment_status", v)}>
              <SelectTrigger className="bg-black/30 border-white/10 text-white mt-1"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="fresher">Fresher</SelectItem>
                <SelectItem value="working_professional">Working professional</SelectItem>
                <SelectItem value="looking_for_job">Looking for a job</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/70">Preferred delivery</Label>
            <Select value={f.preferred_delivery_mode} onValueChange={v => upd("preferred_delivery_mode", v)}>
              <SelectTrigger className="bg-black/30 border-white/10 text-white mt-1"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
                <SelectItem value="blended">Blended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label className="text-white/70">Preferred callback time</Label>
            <Input placeholder="e.g. Weekdays 6-8 pm" value={f.preferred_callback_time} onChange={e => upd("preferred_callback_time", e.target.value)} className="bg-black/30 border-white/10 text-white mt-1"/></div>
          <label className="col-span-2 flex gap-2 text-xs text-white/70 mt-2">
            <input type="checkbox" data-testid="enquiry-consent" checked={f.consent} onChange={e => upd("consent", e.target.checked)} />
            <span>I agree to be contacted by MIRO Academy about this course, and I understand that program details and fees may change.</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/20 text-white hover:bg-white/5">Cancel</Button>
          <Button data-testid="enquiry-submit"
                  disabled={!f.full_name.trim() || !f.phone.trim() || !f.consent || saving}
                  onClick={submit}
                  className="bg-white text-[var(--miro-navy,#041B46)] hover:bg-white/90">
            {saving ? "Sending…" : "Send enquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
